import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import type * as type from "@vTypes";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/cn";
import Modal_shiftCodeList_perType from "../roster_configure/Modal_shiftCodeList_perType";
import { useScheduleStore } from "@store/scheduleStore.ts";
import { Check, RotateCcw, X } from "lucide-react";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import useRosterConfig from "@/hooks/useRosterConfig";
import { Input } from "@/components/ui/input";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { DailyShiftReplaceRequest } from "@/pages/roster-create/service/getDailyShift";
import { replaceDailyShiftApi } from "@/pages/roster-create/services/rosterCreateApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import LoadingSpiner from "@/components/ui/LoadingSpiner";

export type ManpowerDraftSavePayload = {
  monthly: type.ManPowerMonthly;
  daily: type.ManPowerDaily;
  maxEnabled: boolean;
};

export default function ModalManpower({
  onClose,
  availableShifts,
  defaultShiftCode,
  holidayDates,
  officeId,
  groupId,
  year,
  month,
  initialMonthly,
  initialDaily,
  initialMaxEnabled,
  currentShiftManage,
  totalNursesCount,
  draftMode = false,
  isUseMidSaving = false,
  onUseMidChange,
  onDraftSave,
  useMidOverride,
}: {
  onClose: () => void;
  availableShifts: type.availableShifts[];
  defaultShiftCode: type.availableShifts[];
  holidayDates: string[];
  officeId: string;
  groupId: string;
  year: number;
  month: number;
  initialMonthly: type.ManPowerMonthly;
  initialDaily: type.ManPowerDaily;
  initialMaxEnabled: boolean;
  currentShiftManage: type.ShiftManageObject[];
  totalNursesCount: number;
  draftMode?: boolean;
  isUseMidSaving?: boolean;
  onUseMidChange?: (next: boolean) => void;
  onDraftSave?: (payload: ManpowerDraftSavePayload) => void;
  useMidOverride?: boolean;
}) {
  const { curDate } = useScheduleStore();
  const queryClient = useQueryClient();
  const [isShiftCodeModalOpend, setIsShiftCodeModalOpend] =
    useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [dayFilter, setDayFilter] = useState<"all" | "below" | "exceed">("all");
  const [confirmType, setConfirmType] = useState<"monthly" | "daily" | null>(
    null
  );
  /** API 전: 월 기본 '최대'는 화면용 로컬 상태(토글 on일 때만 표시) */
  const [showMaxManpower, setShowMaxManpower] = useState(initialMaxEnabled);
  const [monthlyMaxDraft, setMonthlyMaxDraft] = useState<
    Record<string, number>
  >({});
  const [monthlyDraft, setMonthlyDraft] =
    useState<type.ManPowerMonthly>(initialMonthly);
  const [dailyDraft, setDailyDraft] =
    useState<type.ManPowerDaily>(initialDaily);
  const normalizeDailyArray = (arr: unknown, daysInMonth: number) => {
    const source = Array.isArray(arr) ? arr : [];
    return Array.from({ length: daysInMonth }, (_, idx) => {
      const value = source[idx];
      const num =
        typeof value === "string" ? Number(value) : Number(value ?? 0);
      return Number.isFinite(num) ? num : 0;
    });
  };

  const zeroDailyArray = (daysInMonth: number) =>
    Array.from({ length: daysInMonth }, () => 0);

  const getShiftKey = (shift: type.availableShifts) =>
    shift.default_shift || shift.shift_id;

  const getDaysInMonthForDraft = () => {
    const safeYear = curDate?.year ?? 0;
    const safeMonth = curDate?.month ?? 0;
    const fallbackDays =
      safeYear > 0 && safeMonth > 0
        ? new Date(safeYear, safeMonth, 0).getDate()
        : 31;

    return (
      dailyDraft?.D?.length ||
      dailyDraft?.E?.length ||
      dailyDraft?.N?.length ||
      dailyDraft?.M?.length ||
      fallbackDays
    );
  };

  const buildDailyDraftWithMonthlyDefaults = (
    sourceDaily: type.ManPowerDaily
  ) => {
    const daysInMonth = getDaysInMonthForDraft();
    const next = { ...sourceDaily };

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValue = Number(
        monthlyDraft?.[shiftKey as keyof type.ManPowerMonthly] ?? 0
      );
      const maxValue = Number(monthlyMaxDraft[shiftKey] ?? minValue);

      next[shiftKey] = Array.from({ length: daysInMonth }, () => minValue);

      if (showMaxManpower) {
        next[`${shiftKey}_max`] = Array.from(
          { length: daysInMonth },
          () => maxValue
        );
      }
    }

    return next;
  };

  const validateMonthlyMinMax = () => {
    if (!showMaxManpower) return true;

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValue = Number(
        monthlyDraft?.[shiftKey as keyof type.ManPowerMonthly] ?? 0
      );
      const maxValue = Number(monthlyMaxDraft[shiftKey] ?? minValue);

      if (maxValue > 0 && minValue > maxValue) {
        alert(`${shift.shift_id} 최소 인원은 최대 인원보다 높을 수 없습니다.`);
        return false;
      }
    }

    return true;
  };

  const validateDailyMinMax = (draft: type.ManPowerDaily) => {
    if (!showMaxManpower) return true;

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValues = draft[shiftKey] ?? [];
      const maxValues = draft[`${shiftKey}_max`] ?? [];
      const daysInMonth = Math.max(minValues.length, maxValues.length);

      for (let i = 0; i < daysInMonth; i++) {
        const minValue = Number(minValues[i] ?? 0);
        const maxValue = Number(maxValues[i] ?? minValue);
        if (maxValue > 0 && minValue > maxValue) {
          alert(
            `[${i + 1}일] ${shift.shift_id} 최소 인원은 최대 인원보다 높을 수 없습니다.`
          );
          return false;
        }
      }
    }

    return true;
  };

  const applyMonthlyDraftToDailyDraft = () => {
    const nextDaily = buildDailyDraftWithMonthlyDefaults(dailyDraft);
    setDailyDraft(nextDaily);
  };

  const handleUpdateManpowerMonthly = () => {
    if (!validateMonthlyMinMax()) return;

    const midMonthlyManpower =
      isUseMid === false ? 0 : Number(monthlyDraft?.M || 0);
    const totalMonthlyManpower =
      Number(monthlyDraft?.D || 0) +
      Number(monthlyDraft?.E || 0) +
      Number(monthlyDraft?.N || 0) +
      midMonthlyManpower;

    // 1. 전체 간호사 보다 많으면 안됨!
    if (totalMonthlyManpower > totalNursesCount) {
      alert(`필요인력을 전체 간호사 수보다 많이 지정할 수 없습니다.`);
      return;
    }

    // 2. 경고
    const n = totalNursesCount;
    const threshold = n - (n * 8) / (30 + 3);
    if (totalMonthlyManpower >= threshold) {
      setConfirmType("monthly");
      return;
    }

    applyMonthlyDraftToDailyDraft();
  };

  const handleSaveManpowerDaily = () => {
    if (!validateDailyMinMax(dailyDraft)) return;

    if (dailyDraft.D && dailyDraft.E && dailyDraft.N) {
      const n = totalNursesCount;
      const daysInMonth = dailyDraft.D.length;

      // 1. 각 일 근무 합이 전체근무자 수 보다 많으면 안됨!
      for (let i = 0; i < daysInMonth; i++) {
        const midDailyCount = isUseMid === false ? 0 : dailyDraft.M[i] || 0;
        const dailySum =
          (dailyDraft.D[i] || 0) +
          (dailyDraft.E[i] || 0) +
          (dailyDraft.N[i] || 0) +
          midDailyCount;
        if (dailySum > totalNursesCount) {
          const shiftLabel = isUseMid === false ? "D, E, N" : "D, E, N, M";
          alert(
            `[${
              i + 1
            }일] ${shiftLabel} 근무의 합(${dailySum})이 전체 근무자 수(${totalNursesCount})보다 많을 수 없습니다.`
          );
          return;
        }
      }

      // 2. 경고
      const threshold = n * daysInMonth;
      const isExceeded = dailyDraft.D.some((_, i) => {
        const midDailyCount = isUseMid === false ? 0 : dailyDraft.M[i] || 0;
        const dailySum =
          (dailyDraft.D[i] || 0) +
          (dailyDraft.E[i] || 0) +
          (dailyDraft.N[i] || 0) +
          midDailyCount;
        return dailySum > threshold;
      });

      if (isExceeded) {
        setConfirmType("daily");
        return;
      }
    }
    if (draftMode) {
      onDraftSave?.({
        monthly: monthlyFeedbackBaseline,
        daily: dailyDraft,
        maxEnabled: showMaxManpower,
      });
      onClose();
      return;
    }
    saveAllManpower(dailyDraft);
  };

  const handleConfirmProceed = () => {
    if (confirmType === "monthly") {
      applyMonthlyDraftToDailyDraft();
    } else if (confirmType === "daily") {
      if (draftMode) {
        onDraftSave?.({
          monthly: monthlyFeedbackBaseline,
          daily: dailyDraft,
          maxEnabled: showMaxManpower,
        });
        onClose();
        setConfirmType(null);
        return;
      }
      saveAllManpower(dailyDraft);
    }
    setConfirmType(null);
  };

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = useMidOverride ?? serverConfig?.use_mid;

  const filteredDefaultShiftCode = useMemo(
    () =>
      getDefaultShiftsSortedBySequence(defaultShiftCode, { excludeOff: true }),
    [defaultShiftCode]
  );

  const defaultShiftCodeForManpower = useMemo(
    () =>
      isUseMid !== false
        ? filteredDefaultShiftCode
        : filteredDefaultShiftCode.filter((s) => s.default_shift !== "M"),
    [filteredDefaultShiftCode, isUseMid]
  );

  useEffect(() => {
    setMonthlyDraft(initialMonthly ?? {});
    setMonthlyMaxDraft(() => {
      const next: Record<string, number> = {};
      for (const s of defaultShiftCodeForManpower) {
        const k = getShiftKey(s);
        const maxKey = `${k}_max` as keyof type.ManPowerMonthly;
        const minValue = Number(
          initialMonthly?.[k as keyof type.ManPowerMonthly] ?? 0
        );
        const maxValue = Number(initialMonthly?.[maxKey] ?? minValue);
        next[k] = Number.isFinite(maxValue) ? maxValue : minValue;
      }
      return next;
    });
  }, [defaultShiftCodeForManpower, initialMonthly]);

  useEffect(() => {
    setDailyDraft(initialDaily ?? {});
    setShowMaxManpower(Boolean(initialMaxEnabled));
  }, [initialDaily, initialMaxEnabled]);

  const handleMaxManpowerToggle = (checked: boolean) => {
    setShowMaxManpower(checked);
    if (checked) return;

    setMonthlyMaxDraft((prev) => {
      const next = { ...prev };
      for (const s of defaultShiftCodeForManpower) {
        next[getShiftKey(s)] = 0;
      }
      return next;
    });

    setDailyDraft((prev) => {
      const daysInMonth = getDaysInMonthForDraft();
      const next = { ...prev };
      for (const s of defaultShiftCodeForManpower) {
        next[`${getShiftKey(s)}_max`] = zeroDailyArray(daysInMonth);
      }
      return next;
    });
  };

  const { mutate: saveAllManpower, isPending: isSavingManpower } = useMutation({
    mutationFn: async (draftToSave?: type.ManPowerDaily) => {
      const monthDays = new Date(year, month, 0).getDate();
      const dailyDraftForPayload = draftToSave ?? dailyDraft;
      const dailyPayload: DailyShiftReplaceRequest = {
        office_id: officeId,
        group_id: groupId,
        year,
        month,
        max_enabled: showMaxManpower,
        apply_globally: true,
        date: {
          D_count: normalizeDailyArray(dailyDraftForPayload.D, monthDays),
          E_count: normalizeDailyArray(dailyDraftForPayload.E, monthDays),
          N_count: normalizeDailyArray(dailyDraftForPayload.N, monthDays),
          M_count: normalizeDailyArray(dailyDraftForPayload.M, monthDays),
          D_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.D_max, monthDays)
            : zeroDailyArray(monthDays),
          E_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.E_max, monthDays)
            : zeroDailyArray(monthDays),
          N_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.N_max, monthDays)
            : zeroDailyArray(monthDays),
          M_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.M_max, monthDays)
            : zeroDailyArray(monthDays),
        },
        month_summary: {
          D_count: Number(monthlyFeedbackBaseline.D ?? 0),
          E_count: Number(monthlyFeedbackBaseline.E ?? 0),
          N_count: Number(monthlyFeedbackBaseline.N ?? 0),
          M_count: Number(monthlyFeedbackBaseline.M ?? 0),
          D_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.D_max ?? 0)
            : 0,
          E_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.E_max ?? 0)
            : 0,
          N_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.N_max ?? 0)
            : 0,
          M_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.M_max ?? 0)
            : 0,
          max_enabled: showMaxManpower,
        },
      };
      await replaceDailyShiftApi(dailyPayload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.shifts.shiftManager(groupId),
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.manpower.monthly(officeId, groupId, year, month),
      });
      onClose();
    },
  });

  useEffect(() => {
    setMonthlyMaxDraft((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const s of defaultShiftCodeForManpower) {
        const k = getShiftKey(s);
        const min = Number(
          monthlyDraft?.[k as keyof type.ManPowerMonthly] ?? 0
        );
        if (next[k] === undefined) {
          next[k] = Math.max(min, 0);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defaultShiftCodeForManpower, monthlyDraft]);

  const monthlyFeedbackBaseline = useMemo(() => {
    const next = { ...(monthlyDraft ?? {}) };
    for (const s of defaultShiftCodeForManpower) {
      const k = getShiftKey(s);
      const minValue = Number(
        monthlyDraft?.[k as keyof type.ManPowerMonthly] ?? 0
      );
      next[k as keyof type.ManPowerMonthly] = minValue;
      next[`${k}_max` as keyof type.ManPowerMonthly] = Number(
        monthlyMaxDraft?.[k] ?? minValue
      );
    }
    return next;
  }, [defaultShiftCodeForManpower, monthlyDraft, monthlyMaxDraft]);

  const daysInMonth = getDaysInMonthForDraft();

  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, index) => index + 1),
    [daysInMonth]
  );

  const selectedDayIndex = Math.max(
    0,
    Math.min(selectedDay - 1, Math.max(daysInMonth - 1, 0))
  );

  const getDayMeta = (day: number) => {
    const date = new Date(year, month - 1, day);
    const weekdayIndex = date.getDay();
    const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return {
      label: ["일", "월", "화", "수", "목", "금", "토"][weekdayIndex] ?? "",
      isHoliday: holidayDates.includes(dateKey),
      isSaturday: weekdayIndex === 6,
      isSunday: weekdayIndex === 0,
    };
  };

  const getMonthlyMinValue = (shiftKey: string) =>
    Number(monthlyFeedbackBaseline[shiftKey] ?? 0);

  const getMonthlyMaxValue = (shiftKey: string) =>
    Number(monthlyFeedbackBaseline[`${shiftKey}_max`] ?? 0);

  const getDailyMinValue = (shiftKey: string, dayIndex: number) =>
    Number(dailyDraft[shiftKey]?.[dayIndex] ?? 0);

  const getDailyMaxValue = (shiftKey: string, dayIndex: number) =>
    Number(dailyDraft[`${shiftKey}_max`]?.[dayIndex] ?? 0);

  const getDayStatus = (day: number) => {
    const dayIndex = day - 1;
    let hasException = false;
    let hasBelow = false;
    let hasExceed = false;

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValue = getDailyMinValue(shiftKey, dayIndex);
      const defaultMin = getMonthlyMinValue(shiftKey);

      if (minValue !== defaultMin) {
        hasException = true;
      }
      if (minValue < defaultMin) {
        hasBelow = true;
      }
      if (minValue > defaultMin) {
        hasExceed = true;
      }

      if (!showMaxManpower) continue;

      const maxValue = getDailyMaxValue(shiftKey, dayIndex);
      const defaultMax = getMonthlyMaxValue(shiftKey);
      if (maxValue !== defaultMax) {
        hasException = true;
      }
      if (maxValue > 0 && defaultMax > 0 && maxValue < defaultMax) {
        hasBelow = true;
      }
      if (maxValue > defaultMax) {
        hasExceed = true;
      }
    }

    if (hasBelow && hasExceed) return "mixed";
    if (hasBelow) return "below";
    if (hasExceed) return "exceed";
    if (hasException) return "exception";
    return "default";
  };

  const isDayInActiveFilter = (day: number) => {
    const status = getDayStatus(day);
    if (dayFilter === "all") return true;
    if (dayFilter === "below") {
      return status === "below" || status === "mixed";
    }
    return status === "exceed" || status === "mixed";
  };

  const calendarDays = days;

  const calendarLeadingEmptyCells = new Date(year, month - 1, 1).getDay();
  const calendarTrailingEmptyCells =
    (7 - ((calendarLeadingEmptyCells + calendarDays.length) % 7)) % 7;

  const weekdayLabels = ["일", "월", "화", "수", "목", "금", "토"] as const;

  const exceptionCount = days.filter(
    (day) => getDayStatus(day) !== "default"
  ).length;

  const getDayStatusLabel = (status: ReturnType<typeof getDayStatus>) => {
    if (status === "below") return "미달";
    if (status === "exceed") return "초과";
    if (status === "mixed") return "혼합";
    if (status === "exception") return "예외";
    return null;
  };

  const updateMonthlyMinDraft = (shiftKey: string, nextMin: number) => {
    const previousMin = getMonthlyMinValue(shiftKey);

    setMonthlyDraft((prev) => ({
      ...prev,
      [shiftKey]: nextMin,
    }));

    setDailyDraft((prev) => {
      const current = prev[shiftKey];
      if (!current) return prev;

      const nextValues = current.map((value) =>
        Number(value ?? 0) === previousMin ? nextMin : value
      );

      return { ...prev, [shiftKey]: nextValues };
    });

    if (!showMaxManpower) return;

    setMonthlyMaxDraft((prev) => {
      const prevMax = Number(prev[shiftKey] ?? 0);
      if (prevMax === 0 || prevMax >= nextMin) {
        return prev;
      }
      return { ...prev, [shiftKey]: nextMin };
    });
  };

  const updateMonthlyMaxDraft = (shiftKey: string, nextMax: number) => {
    const previousMax = getMonthlyMaxValue(shiftKey);

    setMonthlyMaxDraft((prev) => ({
      ...prev,
      [shiftKey]: nextMax,
    }));

    setDailyDraft((prev) => {
      const targetKey = `${shiftKey}_max`;
      const current = prev[targetKey];
      if (!current) return prev;

      const nextValues = current.map((value) =>
        Number(value ?? 0) === previousMax ? nextMax : value
      );

      return { ...prev, [targetKey]: nextValues };
    });
  };

  const updateDayValue = (
    shiftKey: string,
    kind: "min" | "max",
    value: number
  ) => {
    const targetKey = kind === "max" ? `${shiftKey}_max` : shiftKey;
    setDailyDraft((prev) => {
      const current = [
        ...(prev[targetKey] ?? Array.from({ length: daysInMonth }, () => 0)),
      ];
      current[selectedDayIndex] = value;

      if (kind === "max" || !showMaxManpower) {
        return { ...prev, [targetKey]: current };
      }

      const maxKey = `${shiftKey}_max`;
      const maxValues = [
        ...(prev[maxKey] ?? Array.from({ length: daysInMonth }, () => 0)),
      ];
      const maxValue = Number(maxValues[selectedDayIndex] ?? 0);
      if (maxValue > 0 && maxValue < value) {
        maxValues[selectedDayIndex] = value;
      }

      return { ...prev, [targetKey]: current, [maxKey]: maxValues };
    });
  };

  const resetDayToMonthlyDefault = (day: number) => {
    const dayIndex = day - 1;
    setDailyDraft((prev) => {
      const next = { ...prev };
      for (const shift of defaultShiftCodeForManpower) {
        const shiftKey = getShiftKey(shift);
        const minValues = [
          ...(next[shiftKey] ?? Array.from({ length: daysInMonth }, () => 0)),
        ];
        minValues[dayIndex] = getMonthlyMinValue(shiftKey);
        next[shiftKey] = minValues;

        const maxKey = `${shiftKey}_max`;
        const maxValues = [
          ...(next[maxKey] ?? Array.from({ length: daysInMonth }, () => 0)),
        ];
        maxValues[dayIndex] = showMaxManpower
          ? getMonthlyMaxValue(shiftKey)
          : 0;
        next[maxKey] = maxValues;
      }
      return next;
    });
  };

  const selectedDayStatus = getDayStatus(selectedDay);
  const selectedDayMeta = getDayMeta(selectedDay);
  const dayFilterOptions = [
    { value: "all", label: "전체" },
    { value: "below", label: "미달" },
    { value: "exceed", label: "초과" },
  ] satisfies ReadonlyArray<{
    readonly value: "all" | "below" | "exceed";
    readonly label: string;
  }>;

  useEffect(() => {
    if (selectedDay <= daysInMonth) return;
    setSelectedDay(Math.max(daysInMonth, 1));
  }, [daysInMonth, selectedDay]);

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          hideCloseButton
          overlayClassName="bg-black/45"
          className="!w-[min(1680px,calc(100vw-48px))] !max-w-none gap-0 overflow-hidden rounded-lg border-0 bg-background p-0 font-pretendard shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
        >
          <DialogTitle className="sr-only">날짜별 근무 인원 설정</DialogTitle>
          <div className="relative flex max-h-[calc(100dvh-32px)] w-full flex-col overflow-hidden bg-white">
            {isSavingManpower && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/50">
                <div className="rounded-lg bg-background px-10 py-5 shadow-2xl">
                  <LoadingSpiner msg="근무인원 설정을 저장하고 있습니다..." />
                </div>
              </div>
            )}
            <header className="relative shrink-0 border-b border-[#EEF2F6] bg-white px-6 py-4">
              <div className="flex items-center justify-between gap-4 pr-10">
                <div className="min-w-0">
                  <h3 className="select-none font-pretendard text-xl font-bold leading-tight text-[#101828]">
                    날짜별 근무 인원 설정
                  </h3>
                  <p className="mt-1 text-xs font-semibold text-[#667085]">
                    기준 인원을 확인하고, 다른 날짜만 선택해 조정합니다.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="absolute right-5 top-4 rounded-md p-1 text-[#98A2B3] transition hover:bg-[#F2F4F7] hover:text-[#344054] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30"
                aria-label="날짜별 근무 인원 설정 닫기"
              >
                <X className="size-6" />
              </button>
            </header>
            <div className="grid min-h-0 grid-cols-[minmax(0,1fr)_380px] overflow-y-auto overflow-x-hidden bg-white">
              <div className="px-6 py-5">
                <section
                  id="daily-manpower-date-section"
                  className="rounded-lg border border-[#E7ECF2] bg-white p-5"
                >
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h4 className="text-lg font-bold tabular-nums leading-none text-foreground">
                      {curDate?.year}년 {curDate?.month}월
                    </h4>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="px-1 text-xs font-bold text-muted-foreground">
                        예외 {exceptionCount}일
                      </span>
                      <div
                        className="flex h-9 items-center rounded-md bg-[#F3F6FA] p-0.5"
                        role="radiogroup"
                        aria-label="날짜 상태 필터"
                      >
                        {dayFilterOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={dayFilter === option.value}
                            onClick={() => setDayFilter(option.value)}
                            className={cn(
                              "h-8 rounded px-4 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30",
                              dayFilter === option.value
                                ? "bg-white text-sig"
                                : "text-muted-foreground hover:text-foreground"
                            )}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 rounded-t-lg border border-b-0 border-[#E1E7EF] bg-[#F8FAFC]">
                    {weekdayLabels.map((label, index) => (
                      <div
                        key={label}
                        className={cn(
                          "border-r border-[#E1E7EF] py-2 text-center text-[12px] font-bold text-muted-foreground last:border-r-0",
                          index === 0 && "text-text-sun",
                          index === 6 && "text-text-sat"
                        )}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 overflow-hidden rounded-b-lg border border-[#E1E7EF] bg-white">
                    {Array.from(
                      { length: calendarLeadingEmptyCells },
                      (_, index) => (
                        <div
                          key={`empty-${index}`}
                          aria-hidden
                          className="min-h-[116px] border-b border-r border-[#E1E7EF] bg-[#F8FAFC] 2xl:min-h-[132px]"
                        />
                      )
                    )}
                    {calendarDays.map((day) => {
                      const status = getDayStatus(day);
                      const meta = getDayMeta(day);
                      const statusLabel = getDayStatusLabel(status);
                      const isSelected = selectedDay === day;
                      const isDimmed = !isDayInActiveFilter(day);
                      const dayIndex = day - 1;
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => setSelectedDay(day)}
                          className={cn(
                            "group flex min-h-[116px] flex-col justify-between border-b border-r border-[#E1E7EF] bg-white px-3 py-3 text-left transition hover:bg-[#F8FBFF] active:translate-y-px focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 2xl:min-h-[132px] 2xl:px-4 2xl:py-4",
                            (status === "below" ||
                              status === "exceed" ||
                              status === "mixed") &&
                              "bg-white",
                            status === "exception" && "bg-[#F8FBFF]",
                            isSelected &&
                              "relative z-10 bg-[#F7FBFF] shadow-[inset_0_0_0_1px_rgba(14,89,198,0.45)]",
                            isDimmed &&
                              "opacity-35 grayscale saturate-0 hover:opacity-70"
                          )}
                        >
                          <div className="flex w-full items-start justify-between gap-2">
                            <span
                              className={cn(
                                "text-lg font-bold leading-none tabular-nums text-foreground 2xl:text-xl",
                                meta.isSaturday && "text-text-sat",
                                (meta.isSunday || meta.isHoliday) &&
                                  "text-text-sun"
                              )}
                            >
                              {day}
                            </span>
                            <div className="flex min-h-5 items-start justify-end gap-1">
                              {statusLabel && (
                                <span
                                  className={cn(
                                    "rounded px-1.5 py-0.5 text-[10px] font-bold leading-none",
                                    status === "below" &&
                                      "bg-[#EAF2FF] text-sig",
                                    status === "exceed" &&
                                      "bg-[#FFF1F0] text-[#E5484D]",
                                    status === "mixed" &&
                                      "bg-[#FFF7E6] text-[#AD6800]",
                                    status === "exception" &&
                                      "bg-[#EFF6FF] text-sig"
                                  )}
                                >
                                  {statusLabel}
                                </span>
                              )}
                              {isSelected && (
                                <span
                                  className="flex size-5 items-center justify-center text-sig"
                                  aria-label="선택된 날짜"
                                  title="선택된 날짜"
                                >
                                  <Check className="size-4.5" strokeWidth={3} />
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            className={cn(
                              "grid w-full gap-1",
                              defaultShiftCodeForManpower.length <= 3
                                ? "grid-cols-3"
                                : "grid-cols-4"
                            )}
                          >
                            {defaultShiftCodeForManpower.map((shift) => {
                              const shiftKey = getShiftKey(shift);
                              const minCount = getDailyMinValue(
                                shiftKey,
                                dayIndex
                              );
                              const maxCount = getDailyMaxValue(
                                shiftKey,
                                dayIndex
                              );
                              const displayCount = showMaxManpower
                                ? `${minCount}/${maxCount || "-"}`
                                : String(minCount);
                              return (
                                <span
                                  key={`${day}-${shiftKey}`}
                                  className="flex min-w-0 flex-col items-center justify-center gap-1.5 rounded bg-white/75 px-1.5 py-1.5 ring-1 ring-[#E7ECF2]"
                                >
                                  <span
                                    className="flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-bold text-white 2xl:size-6 2xl:text-[11px]"
                                    style={{
                                      backgroundColor: shift.color || "#999",
                                      color: getReadableShiftCodeTextColor(shift.color || "#999999"),
                                    }}
                                  >
                                    {shift.shift_id}
                                  </span>
                                  <span className="max-w-full whitespace-nowrap text-[11px] font-semibold leading-tight tabular-nums text-[#344054] 2xl:text-xs">
                                    {displayCount}
                                  </span>
                                </span>
                              );
                            })}
                          </div>
                        </button>
                      );
                    })}
                    {Array.from(
                      { length: calendarTrailingEmptyCells },
                      (_, index) => (
                        <div
                          key={`trailing-empty-${index}`}
                          aria-hidden
                          className="min-h-[116px] border-b border-r border-[#E1E7EF] bg-[#F8FAFC] 2xl:min-h-[132px]"
                        />
                      )
                    )}
                  </div>
                </section>
              </div>

              <aside className="space-y-3 border-l border-[#E7ECF2] bg-[#F8FAFC] px-4 py-5">
                <section className="min-w-0 rounded-lg border border-[#E7ECF2] bg-white p-4">
                  <div className="mb-3 flex min-h-10 items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-sm font-bold text-foreground">
                          월 기본 인원
                        </h4>
                        <span className="rounded bg-[#EFF6FF] px-1.5 py-0.5 text-[10px] font-bold text-sig">
                          기본값
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] font-semibold leading-tight text-muted-foreground">
                        선택 날짜를 되돌릴 때 적용되는 기준입니다.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="h-10 rounded-md bg-[#F3F6FA] px-3.5 text-sm font-bold text-foreground transition hover:bg-[#EEF4FB] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isSavingManpower}
                      onClick={handleUpdateManpowerMonthly}
                    >
                      전체 반영
                    </button>
                  </div>
                  <div className="mb-3 flex items-center gap-1.5">
                    <button
                      type="button"
                      aria-pressed={isUseMid !== false}
                      disabled={isUseMidSaving || !onUseMidChange}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onUseMidChange?.(isUseMid === false);
                      }}
                      className={cn(
                        "inline-flex h-8 flex-1 items-center justify-between gap-2 rounded-md border px-2.5 text-xs font-semibold transition-colors",
                        isUseMid !== false
                          ? "border-[#0E59C6]/40 bg-[#F8FBFF] text-[#0E59C6]"
                          : "border-slate-200 bg-white text-slate-600",
                        "hover:border-[#0E59C6]/40 hover:bg-[#F5F8FC]",
                        "disabled:cursor-not-allowed disabled:opacity-60",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                      )}
                      title={`M 근무 ${isUseMid !== false ? "사용" : "미사용"}`}
                    >
                      <span>M 근무</span>
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[10px] font-bold",
                          isUseMid !== false
                            ? "bg-[#EAF2FF] text-[#0E59C6]"
                            : "bg-slate-100 text-slate-500"
                        )}
                      >
                        {isUseMid !== false ? "ON" : "OFF"}
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-pressed={showMaxManpower}
                      onClick={() => handleMaxManpowerToggle(!showMaxManpower)}
                      className={cn(
                        "inline-flex h-8 flex-1 items-center justify-between gap-2 rounded-md border px-2.5 text-xs font-semibold transition-colors",
                        showMaxManpower
                          ? "border-[#0E59C6]/40 bg-[#F8FBFF] text-[#0E59C6]"
                          : "border-slate-200 bg-white text-slate-600",
                        "hover:border-[#0E59C6]/40 hover:bg-[#F5F8FC]",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                      )}
                      title={`최대 인원 ${showMaxManpower ? "사용" : "미사용"}`}
                    >
                      <span>최대 인원</span>
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[10px] font-bold",
                          showMaxManpower
                            ? "bg-[#EAF2FF] text-[#0E59C6]"
                            : "bg-slate-100 text-slate-500"
                        )}
                      >
                        {showMaxManpower ? "ON" : "OFF"}
                      </span>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {defaultShiftCodeForManpower.map((shifts) => {
                      const shiftKey = shifts.default_shift || shifts.shift_id;
                      const minVal =
                        monthlyDraft?.[shiftKey as keyof type.ManPowerMonthly];
                      const maxVal =
                        monthlyMaxDraft[shiftKey] ?? Number(minVal ?? 0);
                      return (
                        <div
                          key={shiftKey}
                          className="flex min-w-0 items-center gap-1.5 rounded-md border border-[#EEF2F6] bg-[#F8FAFC] px-2 py-1.5"
                        >
                          <button
                            type="button"
                            className="flex size-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white"
                            style={{ backgroundColor: shifts.color || "#999", color: getReadableShiftCodeTextColor(shifts.color || "#999999") }}
                            onClick={() => setIsShiftCodeModalOpend(true)}
                            title="근무 코드 설정 보기"
                          >
                            {shifts.shift_id}
                          </button>
                          <Input
                            type="text"
                            inputMode="numeric"
                            aria-label={`${shiftKey} 기준 최소 인원`}
                            className="h-7 min-w-0 flex-1 rounded-md border bg-white px-1 text-center text-xs! font-bold tabular-nums"
                            value={minVal ?? ""}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^0-9]/g, "");
                              const nextMin = v === "" ? 0 : Number(v);
                              updateMonthlyMinDraft(shiftKey, nextMin);
                            }}
                            onBlur={(e) => {
                              if (e.currentTarget.value === "") {
                                updateMonthlyMinDraft(shiftKey, 0);
                              }
                            }}
                          />
                          {showMaxManpower && (
                            <>
                              <span className="text-[10px] font-semibold text-muted-foreground">
                                /
                              </span>
                              <Input
                                type="text"
                                inputMode="numeric"
                                aria-label={`${shiftKey} 기준 최대 인원`}
                                className="h-7 min-w-0 flex-1 rounded-md border bg-white px-1 text-center text-xs! font-bold tabular-nums"
                                value={Number(maxVal) === 0 ? "-" : maxVal}
                                onChange={(e) => {
                                  const v = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  const nextMax = v === "" ? 0 : Number(v);
                                  updateMonthlyMaxDraft(shiftKey, nextMax);
                                }}
                                onBlur={() => {
                                  const minValue = Number(minVal ?? 0);
                                  const currentMax = Number(maxVal ?? 0);
                                  if (currentMax > 0 && currentMax < minValue) {
                                    updateMonthlyMaxDraft(shiftKey, minValue);
                                  }
                                }}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
                <section className="min-w-0 rounded-lg border border-[#E7ECF2] bg-white p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <span className="mb-1 inline-flex rounded bg-[#EFF6FF] px-1.5 py-0.5 text-[10px] font-bold text-sig">
                        선택된 날짜
                      </span>
                      <h4 className="text-lg font-bold tabular-nums text-foreground">
                        {selectedDay}일 {selectedDayMeta.label}요일
                      </h4>
                      <p className="mt-0.5 text-[11px] font-semibold text-muted-foreground">
                        이 날짜만 월 기본값과 다르게 조정합니다.
                      </p>
                    </div>
                    {selectedDayStatus !== "default" && (
                      <span
                        className={cn(
                          "rounded-md px-2 py-1 text-[11px] font-bold",
                          selectedDayStatus === "below" &&
                            "bg-[#EAF2FF] text-sig",
                          selectedDayStatus === "exceed" &&
                            "bg-[#FFF1F0] text-[#E5484D]",
                          selectedDayStatus === "mixed" &&
                            "bg-[#FFF7E6] text-[#AD6800]",
                          selectedDayStatus === "exception" &&
                            "bg-[#EFF6FF] text-sig"
                        )}
                      >
                        {selectedDayStatus === "below"
                          ? "미달"
                          : selectedDayStatus === "exceed"
                            ? "초과"
                            : selectedDayStatus === "mixed"
                              ? "혼합"
                              : "예외"}
                      </span>
                    )}
                  </div>
                  <div className="overflow-hidden rounded-md border border-[#E7ECF2]">
                    <div
                      className={cn(
                        "grid bg-[#F8FAFC] px-3 py-1.5 text-[11px] font-bold text-muted-foreground",
                        showMaxManpower
                          ? "grid-cols-[44px_minmax(56px,1fr)_minmax(56px,1fr)_44px]"
                          : "grid-cols-[44px_minmax(80px,1fr)_44px]"
                      )}
                    >
                      <span>근무</span>
                      <span className="text-center">최소</span>
                      {showMaxManpower && (
                        <span className="text-center">최대</span>
                      )}
                      <span className="text-right">월 기준</span>
                    </div>
                    {defaultShiftCodeForManpower.map((shifts) => {
                      const shiftKey = shifts.default_shift || shifts.shift_id;
                      const minValue = getDailyMinValue(
                        shiftKey,
                        selectedDayIndex
                      );
                      const maxValue = getDailyMaxValue(
                        shiftKey,
                        selectedDayIndex
                      );
                      const defaultMin = getMonthlyMinValue(shiftKey);
                      const defaultMax = getMonthlyMaxValue(shiftKey);
                      return (
                        <div
                          key={shiftKey}
                          className={cn(
                            "grid items-center gap-2 border-t border-[#E7ECF2] bg-white px-3 py-2",
                            showMaxManpower
                              ? "grid-cols-[44px_minmax(56px,1fr)_minmax(56px,1fr)_44px]"
                              : "grid-cols-[44px_minmax(80px,1fr)_44px]"
                          )}
                        >
                          <div className="flex min-w-0 items-center">
                            <div
                              className="flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white"
                              style={{
                                backgroundColor: shifts.color || "#999",
                                color: getReadableShiftCodeTextColor(shifts.color || "#999999"),
                              }}
                            >
                              {shifts.shift_id}
                            </div>
                          </div>
                          <Input
                            type="text"
                            inputMode="numeric"
                            aria-label={`${selectedDay}일 ${shiftKey} 최소 인원`}
                            className={cn(
                              "h-8 min-w-0 rounded-md border bg-background px-1 text-center text-sm! font-bold tabular-nums outline-none focus:border-sig focus:ring-1 focus:ring-sig/20",
                              minValue < defaultMin &&
                                "border-manpower-below-border bg-manpower-below-bg",
                              minValue > defaultMin &&
                                "border-manpower-exceed-border bg-manpower-exceed-bg"
                            )}
                            value={minValue}
                            onChange={(e) => {
                              const parsed = Number(
                                e.target.value.replace(/\D/g, "") || 0
                              );
                              updateDayValue(shiftKey, "min", parsed);
                            }}
                          />
                          {showMaxManpower && (
                            <Input
                              type="text"
                              inputMode="numeric"
                              aria-label={`${selectedDay}일 ${shiftKey} 최대 인원`}
                              className={cn(
                                "h-8 min-w-0 rounded-md border bg-background px-1 text-center text-sm! font-bold tabular-nums outline-none focus:border-sig focus:ring-1 focus:ring-sig/20",
                                maxValue > 0 &&
                                  maxValue < defaultMax &&
                                  "border-manpower-below-border bg-manpower-below-bg",
                                maxValue > defaultMax &&
                                  "border-manpower-exceed-border bg-manpower-exceed-bg"
                              )}
                              value={Number(maxValue) === 0 ? "-" : maxValue}
                              onChange={(e) => {
                                const parsed = Number(
                                  e.target.value.replace(/\D/g, "") || 0
                                );
                                updateDayValue(shiftKey, "max", parsed);
                              }}
                              onBlur={() => {
                                if (maxValue > 0 && maxValue < minValue) {
                                  updateDayValue(shiftKey, "max", minValue);
                                }
                              }}
                            />
                          )}
                          <span className="truncate text-right text-[11px] font-semibold tabular-nums text-muted-foreground">
                            {defaultMin}
                            {showMaxManpower
                              ? ` / ${defaultMax > 0 ? defaultMax : "-"}`
                              : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => resetDayToMonthlyDefault(selectedDay)}
                    className="mt-3 flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-[#F3F6FA] text-xs font-bold text-foreground transition hover:bg-[#EEF4FB] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30"
                  >
                    <RotateCcw className="size-3.5" />
                    선택 날짜 기본값으로 되돌리기
                  </button>
                </section>
              </aside>
            </div>
            <footer className="relative shrink-0 border-t border-[#E4E7EC] bg-white px-6 py-4">
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 rounded-md bg-[#EEF2F6] px-5 text-sm font-bold text-[#344054] transition hover:bg-[#E4EAF1]"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleSaveManpowerDaily}
                  className="h-10 rounded-md bg-sig px-7 text-sm font-bold text-white transition hover:opacity-90"
                >
                  저장하기
                </button>
              </div>
            </footer>
          </div>
        </DialogContent>
      </Dialog>
      {isShiftCodeModalOpend && (
        <Modal_shiftCodeList_perType
          availableShifts={availableShifts}
          currentShiftManage={currentShiftManage}
          onEditShiftCodes={() => {
            setIsShiftCodeModalOpend(false);
          }}
          setIsShiftCodeModalOpend={setIsShiftCodeModalOpend}
        />
      )}
      <Dialog
        open={confirmType !== null}
        onOpenChange={(open) => !open && setConfirmType(null)}
      >
        <DialogContent
          overlayClassName="bg-black/45"
          className="z-[80] w-[min(420px,calc(100vw-40px))] gap-0 rounded-xl border-0 bg-white p-0 font-pretendard shadow-[0_20px_60px_rgba(15,23,42,0.26)]"
        >
          <div className="px-6 py-5">
            <DialogTitle className="text-lg font-bold text-[#101828]">
              계속 진행하시겠습니까?
            </DialogTitle>
            <DialogDescription className="mt-3 whitespace-pre-line text-sm font-semibold leading-relaxed text-[#667085]">
              {confirmType === "daily"
                ? "일자별 필요 인력이 많아 위반사항이 많아질 수 있습니다.\n계속하시겠습니까?"
                : "위반사항이 많아질 수 있습니다. 계속하시겠습니까?"}
            </DialogDescription>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#E4E7EC] px-6 py-4">
            <button
              type="button"
              onClick={() => setConfirmType(null)}
              className="h-10 rounded-md bg-[#EEF2F6] px-5 text-sm font-bold text-[#344054] transition hover:bg-[#E4EAF1]"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleConfirmProceed}
              className="h-10 rounded-md bg-sig px-5 text-sm font-bold text-white transition hover:opacity-90"
            >
              계속
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
